import * as React from 'react'
import './styles.css'
import { motion } from 'motion/react'
import {
  GlobeAltIcon,
  MapIcon,
  ArrowsRightLeftIcon,
  UserGroupIcon,
  BriefcaseIcon,
  SparklesIcon,
  AcademicCapIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline'
import {
  descriptionAnimation,
  titleAnimation,
} from '../../../constants/animation'
import PlatformRibbon from '../../ui/PlatformRibbon'
import TrademarkText from '../../ui/TrademarkText'

const PlatformArchitecture = () => {
  return (
    <section className='platform-architecture-section'>
      <PlatformRibbon />
      <div className='box'>
        <div className='platform-architecture-content'>
          <motion.h2
            className='platform-architecture-title'
            initial='hidden'
            animate='visible'
            variants={titleAnimation}
          >
            A Modular Workforce Intelligence Architecture
          </motion.h2>

          <motion.p
            className='platform-architecture-copy'
            initial='hidden'
            animate='visible'
            variants={descriptionAnimation}
          >
            Patent-Pending Deterministic Intelligence Architecture (USPTO 2026)
          </motion.p>

          <motion.p
            className='platform-architecture-copy'
            initial='hidden'
            animate='visible'
            variants={descriptionAnimation}
          >
            Pod is built as a modular workforce intelligence platform designed
            for complex, multi-location and mission-critical organizations.
          </motion.p>

          <motion.p
            className='platform-architecture-copy'
            initial='hidden'
            animate='visible'
            variants={descriptionAnimation}
          >
            <TrademarkText>Pod Intelligence Core™</TrademarkText> - Central AI
            brain and skills intelligence engine
          </motion.p>
          <motion.p
            className='platform-architecture-copy'
            initial='hidden'
            animate='visible'
            variants={descriptionAnimation}
          >
            <TrademarkText>Pod Atlas™</TrademarkText> - Spatial workforce
            mapping and skills visibility
          </motion.p>
          <motion.p
            className='platform-architecture-copy'
            initial='hidden'
            animate='visible'
            variants={descriptionAnimation}
          >
            <TrademarkText>Pod Matching Engine™</TrademarkText> - AI alignment
            of people, skills, and opportunities
          </motion.p>
          <motion.p
            className='platform-architecture-copy'
            initial='hidden'
            animate='visible'
            variants={descriptionAnimation}
          >
            <TrademarkText>Pod Adaptive Intelligence Engine™</TrademarkText> -
            Adaptive AI pathways and predictive workforce intelligence
          </motion.p>

          <motion.p
            className='platform-architecture-copy'
            initial='hidden'
            animate='visible'
            variants={descriptionAnimation}
          >
            Together, these modules form a unified intelligence architecture
            that sits above existing systems - enhancing them rather than
            replacing them.
          </motion.p>

          <div className='platform-architecture-diagram'>
            <div className='platform-diagram-node platform-diagram-node-source'>
              <div className='platform-diagram-title'>
                Existing Enterprise Systems &amp; Data Sources
              </div>
              <div className='platform-diagram-subtitle'>
                ATS, HRIS, LMS, CRM, DoD, Universities, External Data
              </div>
            </div>

            <div className='platform-diagram-arrow' aria-hidden='true'>
              <span className='platform-diagram-arrow-line' />
              <span className='platform-diagram-arrow-head' />
            </div>

            <div className='platform-diagram-node platform-diagram-node-core'>
              <div className='platform-diagram-title'>
                Pod Intelligence Core™
              </div>
              <div className='platform-diagram-subtitle'>
                Skills Graph, AI Models, Taxonomy
              </div>
            </div>

            <div className='platform-diagram-fork'>
              <span className='platform-diagram-fork-line' />
              <span
                className='platform-diagram-fork-arrow platform-diagram-fork-arrow-left'
                aria-hidden='true'
              >
                <span className='platform-diagram-arrow-line' />
                <span className='platform-diagram-arrow-head' />
              </span>
              <span className='platform-diagram-fork-arrow' aria-hidden='true'>
                <span className='platform-diagram-arrow-line' />
                <span className='platform-diagram-arrow-head' />
              </span>
              <span
                className='platform-diagram-fork-arrow platform-diagram-fork-arrow-right'
                aria-hidden='true'
              >
                <span className='platform-diagram-arrow-line' />
                <span className='platform-diagram-arrow-head' />
              </span>
            </div>

            <div className='platform-diagram-node platform-diagram-node-module platform-diagram-node-module-atlas'>
              <div className='platform-diagram-title'>
                Pod Adaptive Intelligence Engine™
              </div>
              <div className='platform-diagram-features'>
                <div className='platform-diagram-feature-row'>
                  <SparklesIcon className='platform-diagram-feature-icon' />
                  <span>Adaptive AI &amp; Personalization</span>
                </div>
                <div className='platform-diagram-feature-row'>
                  <AcademicCapIcon className='platform-diagram-feature-icon' />
                  <span>Career Pathways</span>
                </div>
                <div className='platform-diagram-feature-row'>
                  <ChartBarIcon className='platform-diagram-feature-icon' />
                  <span>Predictions</span>
                </div>
              </div>
            </div>

            <div className='platform-diagram-node platform-diagram-node-module platform-diagram-node-module-matching'>
              <div className='platform-diagram-title'>
                Pod Matching Engine™
              </div>
              <div className='platform-diagram-features'>
                <div className='platform-diagram-feature-row'>
                  <BriefcaseIcon className='platform-diagram-feature-icon' />
                  <span>Jobs ⇄ People</span>
                </div>
                <div className='platform-diagram-feature-row'>
                  <UserGroupIcon className='platform-diagram-feature-icon' />
                  <span>Skills Alignment</span>
                </div>
                <div className='platform-diagram-feature-row platform-diagram-feature-row-stacked'>
                  <SparklesIcon className='platform-diagram-feature-icon' />
                  <span className='platform-diagram-feature-stack'>
                    <span className='platform-diagram-feature-label'>
                      Deterministic AI Matching Layer
                    </span>
                    <span className='platform-diagram-feature-badge'>
                      Patent Pending
                    </span>
                  </span>
                </div>
              </div>
            </div>

            <div className='platform-diagram-node platform-diagram-node-module platform-diagram-node-module-adaptive'>
              <div className='platform-diagram-title'>Pod Atlas™</div>
              <div className='platform-diagram-features'>
                <div className='platform-diagram-feature-row'>
                  <GlobeAltIcon className='platform-diagram-feature-icon' />
                  <span>Spatial &amp; Talent Mapping</span>
                </div>
                <div className='platform-diagram-feature-row'>
                  <MapIcon className='platform-diagram-feature-icon' />
                  <span>Geo Heatmaps</span>
                </div>
                <div className='platform-diagram-feature-row'>
                  <ArrowsRightLeftIcon className='platform-diagram-feature-icon' />
                  <span>Supply &amp; Demand</span>
                </div>
              </div>
            </div>
          </div>

          <motion.p
            className='platform-architecture-copy'
            initial='hidden'
            animate='visible'
            variants={descriptionAnimation}
          >
            Pod is built on a proprietary, patent-pending deterministic
            intelligence architecture designed for complex, multi-location, and
            mission-critical organizations.
          </motion.p>
          <motion.p
            className='platform-architecture-copy'
            initial='hidden'
            animate='visible'
            variants={descriptionAnimation}
          >
            The platform ingests data from existing enterprise systems (ATS,
            HRIS, LMS, CRM, government, and institutional datasets) and
            structures it into a unified intelligence layer powered by
            deterministic matching, structured cohort dataset generation, and
            explainable analytical models.
          </motion.p>
          <motion.p
            className='platform-architecture-copy'
            initial='hidden'
            animate='visible'
            variants={descriptionAnimation}
          >
            Patent-Pending Components (USPTO 2026): Deterministic Matching
            Engine, Structured Cohort Dataset Generation, and Interactive
            Intelligence Interface.
          </motion.p>
        </div>
      </div>
    </section>
  )
}

export default PlatformArchitecture
